import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, Lock, Shield, CheckCircle, ArrowLeft, Package, MapPin, Truck, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/Button';
type PaymentMethod = 'card' | 'paypal' | 'stripe';
interface PaymentPageProps {
  onBack?: () => void;
  onComplete?: () => void;
  orderSummary?: {
    total: number;
    packageCount: number;
    route: string;
    packageType: string;
  };
}
export function PaymentPage({
  onBack,
  onComplete,
  orderSummary = {
    total: 127.5,
    packageCount: 2,
    route: 'New York, USA → Lagos, Nigeria',
    packageType: 'Electronics'
  }
}: PaymentPageProps) {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [cardData, setCardData] = useState({
    number: '',
    expiry: '',
    cvv: '',
    name: ''
  });
  const [billingAddress, setBillingAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States'
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [mobileOrderExpanded, setMobileOrderExpanded] = useState(false);
  const handleCardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    let formattedValue = value;
    if (name === 'number') {
      formattedValue = value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim().slice(0, 19);
    }
    if (name === 'expiry') {
      formattedValue = value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1/$2').slice(0, 5);
    }
    if (name === 'cvv') {
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    }
    setCardData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  };
  const handleBillingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setBillingAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async () => {
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsProcessing(false);
    onComplete?.();
  };
  const isFormValid = paymentMethod === 'card' ? cardData.number.length >= 19 && cardData.expiry.length === 5 && cardData.cvv.length >= 3 && cardData.name && agreedToTerms : agreedToTerms;
  return <div className="min-h-screen bg-gray-50 py-6 sm:py-12 pb-32 lg:pb-12" data-id="element-388">
      <div className="max-w-6xl mx-auto px-4" data-id="element-389">
        {/* Header */}
        <div className="mb-6 sm:mb-8" data-id="element-390">
          <button onClick={onBack} className="flex items-center gap-2 text-gray-600 active:text-gray-900 mb-3 sm:mb-4 transition-colors p-1 -ml-1" data-id="element-391">
            <ArrowLeft className="w-4 h-4" data-id="element-392" />
            <span className="text-sm" data-id="element-393">Back</span>
          </button>
          <h1 className="text-xl sm:text-3xl font-bold text-gray-900" data-id="element-394">
            Secure Checkout
          </h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base" data-id="element-395">
            Complete your payment to confirm your shipment
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-396">
          {/* Main Payment Form */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6" data-id="element-397">
            {/* Mobile Order Summary (Collapsible) */}
            <div className="lg:hidden bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-398">
              <button onClick={() => setMobileOrderExpanded(!mobileOrderExpanded)} className="w-full p-4 flex items-center justify-between" data-id="element-399">
                <div className="flex items-center gap-3" data-id="element-400">
                  <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center" data-id="element-401">
                    <Package className="w-5 h-5 text-brand" data-id="element-402" />
                  </div>
                  <div className="text-left" data-id="element-403">
                    <span className="text-xs text-gray-500 block" data-id="element-404">
                      Order Total
                    </span>
                    <span className="text-lg font-bold text-brand" data-id="element-405">
                      ${orderSummary.total.toFixed(2)}
                    </span>
                  </div>
                </div>
                {mobileOrderExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-406" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-407" />}
              </button>

              <AnimatePresence data-id="element-408">
                {mobileOrderExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden border-t border-gray-100" data-id="element-409">
                    <div className="p-4 bg-gray-50 space-y-3 text-sm" data-id="element-410">
                      <div className="flex items-center gap-2" data-id="element-411">
                        <Truck className="w-4 h-4 text-gray-400" data-id="element-412" />
                        <span className="text-gray-600" data-id="element-413">
                          {orderSummary.route}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-414">
                        <span className="text-gray-500" data-id="element-415">Package Type</span>
                        <span className="font-medium" data-id="element-416">
                          {orderSummary.packageType}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-417">
                        <span className="text-gray-500" data-id="element-418">Packages</span>
                        <span className="font-medium" data-id="element-419">
                          {orderSummary.packageCount}
                        </span>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Payment Method Selection */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 p-4 sm:p-6 shadow-sm" data-id="element-420">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-421">
                <CreditCard className="w-5 h-5 text-brand" data-id="element-422" />
                Payment Method
              </h2>

              <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6" data-id="element-423">
                <button onClick={() => setPaymentMethod('card')} className={`p-3 sm:p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-1 sm:gap-2 ${paymentMethod === 'card' ? 'border-brand bg-brand-50' : 'border-gray-200 active:border-gray-300'}`} data-id="element-424">
                  <div className="flex gap-1" data-id="element-425">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/100px-Visa_Inc._logo.svg.png" alt="Visa" className="h-4 sm:h-5 object-contain" data-id="element-426" />
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/100px-Mastercard-logo.svg.png" alt="Mastercard" className="h-4 sm:h-5 object-contain" data-id="element-427" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-medium text-gray-700" data-id="element-428">
                    Card
                  </span>
                </button>

                <button onClick={() => setPaymentMethod('paypal')} className={`p-3 sm:p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-1 sm:gap-2 ${paymentMethod === 'paypal' ? 'border-brand bg-brand-50' : 'border-gray-200 active:border-gray-300'}`} data-id="element-429">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/PayPal.svg/100px-PayPal.svg.png" alt="PayPal" className="h-4 sm:h-5 object-contain" data-id="element-430" />
                  <span className="text-[10px] sm:text-xs font-medium text-gray-700" data-id="element-431">
                    PayPal
                  </span>
                </button>

                <button onClick={() => setPaymentMethod('stripe')} className={`p-3 sm:p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-1 sm:gap-2 ${paymentMethod === 'stripe' ? 'border-brand bg-brand-50' : 'border-gray-200 active:border-gray-300'}`} data-id="element-432">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Stripe_Logo%2C_revised_2016.svg/100px-Stripe_Logo%2C_revised_2016.svg.png" alt="Stripe" className="h-4 sm:h-5 object-contain" data-id="element-433" />
                  <span className="text-[10px] sm:text-xs font-medium text-gray-700" data-id="element-434">
                    Stripe
                  </span>
                </button>
              </div>

              {/* Card Input Fields */}
              {paymentMethod === 'card' && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} className="space-y-3 sm:space-y-4" data-id="element-435">
                  <div className="space-y-2" data-id="element-436">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-437">
                      Card Number
                    </label>
                    <div className="relative" data-id="element-438">
                      <input type="text" inputMode="numeric" name="number" value={cardData.number} onChange={handleCardChange} placeholder="1234 5678 9012 3456" className="w-full px-4 py-3.5 sm:py-3 pl-12 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" data-id="element-439" />
                      <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-440" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-441">
                    <div className="space-y-2" data-id="element-442">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-443">
                        Expiry
                      </label>
                      <input type="text" inputMode="numeric" name="expiry" value={cardData.expiry} onChange={handleCardChange} placeholder="MM/YY" className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" data-id="element-444" />
                    </div>
                    <div className="space-y-2" data-id="element-445">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-446">
                        CVV
                      </label>
                      <div className="relative" data-id="element-447">
                        <input type="text" inputMode="numeric" name="cvv" value={cardData.cvv} onChange={handleCardChange} placeholder="123" className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" data-id="element-448" />
                        <button className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 p-1" data-id="element-449">
                          <Info className="w-4 h-4" data-id="element-450" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-451">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-452">
                      Cardholder Name
                    </label>
                    <input type="text" name="name" value={cardData.name} onChange={handleCardChange} placeholder="John Doe" className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" data-id="element-453" />
                  </div>
                </motion.div>}

              {paymentMethod !== 'card' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-center" data-id="element-454">
                  <p className="text-gray-600 text-sm" data-id="element-455">
                    You will be redirected to{' '}
                    {paymentMethod === 'paypal' ? 'PayPal' : 'Stripe'} to
                    complete your payment.
                  </p>
                </motion.div>}
            </motion.div>

            {/* Billing Address */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 p-4 sm:p-6 shadow-sm" data-id="element-456">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-457">
                <MapPin className="w-5 h-5 text-brand" data-id="element-458" />
                Billing Address
              </h2>

              <div className="space-y-3 sm:space-y-4" data-id="element-459">
                <div className="space-y-2" data-id="element-460">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-461">
                    Street Address
                  </label>
                  <input type="text" name="street" value={billingAddress.street} onChange={handleBillingChange} placeholder="123 Main Street" className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" data-id="element-462" />
                </div>

                <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-463">
                  <div className="space-y-2" data-id="element-464">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-465">
                      City
                    </label>
                    <input type="text" name="city" value={billingAddress.city} onChange={handleBillingChange} placeholder="New York" className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" data-id="element-466" />
                  </div>
                  <div className="space-y-2" data-id="element-467">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-468">
                      State
                    </label>
                    <input type="text" name="state" value={billingAddress.state} onChange={handleBillingChange} placeholder="NY" className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" data-id="element-469" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-470">
                  <div className="space-y-2" data-id="element-471">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-472">
                      ZIP Code
                    </label>
                    <input type="text" inputMode="numeric" name="zip" value={billingAddress.zip} onChange={handleBillingChange} placeholder="10001" className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" data-id="element-473" />
                  </div>
                  <div className="space-y-2" data-id="element-474">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-475">
                      Country
                    </label>
                    <select name="country" value={billingAddress.country} onChange={handleBillingChange} className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand outline-none bg-white text-gray-900 text-base" data-id="element-476">
                      <option value="United States" data-id="element-477">United States</option>
                      <option value="Canada" data-id="element-478">Canada</option>
                      <option value="United Kingdom" data-id="element-479">United Kingdom</option>
                    </select>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Terms (Desktop) */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="hidden lg:block space-y-4" data-id="element-480">
              <label className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors" data-id="element-481">
                <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-0.5 w-5 h-5 rounded text-brand focus:ring-brand border-gray-300" data-id="element-482" />
                <span className="text-sm text-gray-700" data-id="element-483">
                  I agree to the{' '}
                  <a href="#" className="text-brand underline" data-id="element-484">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="#" className="text-brand underline" data-id="element-485">
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>

              <Button fullWidth size="lg" onClick={handleSubmit} disabled={!isFormValid || isProcessing} className="gap-2" data-id="element-486">
                {isProcessing ? <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-487" />
                    Processing...
                  </> : <>
                    <Lock className="w-5 h-5" data-id="element-488" />
                    Complete Payment - ${orderSummary.total.toFixed(2)}
                  </>}
              </Button>

              <button onClick={onBack} className="w-full text-center text-gray-500 hover:text-gray-700 text-sm py-2 transition-colors" data-id="element-489">
                Cancel and return to shipping
              </button>
            </motion.div>
          </div>

          {/* Order Summary Sidebar (Desktop) */}
          <div className="hidden lg:block lg:col-span-1" data-id="element-490">
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} className="sticky top-24 space-y-6" data-id="element-491">
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-492">
                <div className="bg-secondary-900 p-4 text-white" data-id="element-493">
                  <h3 className="font-semibold flex items-center gap-2" data-id="element-494">
                    <Package className="w-5 h-5" data-id="element-495" />
                    Order Summary
                  </h3>
                </div>

                <div className="p-5 space-y-4" data-id="element-496">
                  <div className="flex items-start gap-3 pb-4 border-b border-gray-100" data-id="element-497">
                    <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center" data-id="element-498">
                      <Truck className="w-5 h-5 text-brand" data-id="element-499" />
                    </div>
                    <div className="flex-1" data-id="element-500">
                      <p className="font-medium text-gray-900 text-sm" data-id="element-501">
                        {orderSummary.route}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-502">
                        {orderSummary.packageType} • {orderSummary.packageCount}{' '}
                        package(s)
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm" data-id="element-503">
                    <div className="flex justify-between" data-id="element-504">
                      <span className="text-gray-600" data-id="element-505">Subtotal</span>
                      <span className="font-medium" data-id="element-506">
                        ${(orderSummary.total * 0.9).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-507">
                      <span className="text-gray-600" data-id="element-508">Insurance</span>
                      <span className="font-medium" data-id="element-509">
                        ${(orderSummary.total * 0.05).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-510">
                      <span className="text-gray-600" data-id="element-511">Processing Fee</span>
                      <span className="font-medium" data-id="element-512">
                        ${(orderSummary.total * 0.05).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-100 flex justify-between items-end" data-id="element-513">
                    <span className="font-bold text-gray-900" data-id="element-514">Total</span>
                    <span className="text-2xl font-bold text-brand" data-id="element-515">
                      ${orderSummary.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Security Badges */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm" data-id="element-516">
                <div className="flex items-center gap-2 mb-4" data-id="element-517">
                  <Shield className="w-5 h-5 text-success" data-id="element-518" />
                  <span className="font-semibold text-gray-900 text-sm" data-id="element-519">
                    Secure Payment
                  </span>
                </div>

                <div className="space-y-3" data-id="element-520">
                  <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-521">
                    <Lock className="w-4 h-4 text-success" data-id="element-522" />
                    <span data-id="element-523">256-bit SSL Encryption</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-524">
                    <CheckCircle className="w-4 h-4 text-success" data-id="element-525" />
                    <span data-id="element-526">PCI DSS Compliant</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-527">
                    <Shield className="w-4 h-4 text-success" data-id="element-528" />
                    <span data-id="element-529">Fraud Protection</span>
                  </div>
                </div>
              </div>

              {/* Money-back Guarantee */}
              <div className="bg-success-light rounded-xl p-4 border border-success/20" data-id="element-530">
                <div className="flex items-start gap-3" data-id="element-531">
                  <div className="w-8 h-8 bg-success rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-532">
                    <CheckCircle className="w-4 h-4 text-white" data-id="element-533" />
                  </div>
                  <div data-id="element-534">
                    <p className="font-semibold text-gray-900 text-sm" data-id="element-535">
                      Money-Back Guarantee
                    </p>
                    <p className="text-xs text-gray-600 mt-1" data-id="element-536">
                      Full refund if your shipment doesn't arrive as promised.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Mobile Fixed Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-40" data-id="element-537">
        <label className="flex items-start gap-3 mb-3 cursor-pointer" data-id="element-538">
          <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-0.5 w-5 h-5 rounded text-brand focus:ring-brand border-gray-300" data-id="element-539" />
          <span className="text-xs text-gray-600" data-id="element-540">
            I agree to the{' '}
            <a href="#" className="text-brand" data-id="element-541">
              Terms
            </a>{' '}
            and{' '}
            <a href="#" className="text-brand" data-id="element-542">
              Privacy Policy
            </a>
          </span>
        </label>

        <Button fullWidth size="lg" onClick={handleSubmit} disabled={!isFormValid || isProcessing} className="gap-2 h-12" data-id="element-543">
          {isProcessing ? <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-544" />
              Processing...
            </> : <>
              <Lock className="w-5 h-5" data-id="element-545" />
              Pay ${orderSummary.total.toFixed(2)}
            </>}
        </Button>
      </div>
    </div>;
}